import { useCallback, useEffect, useState } from 'react';
import DocumentService, { FileFolder } from '../../api/services/document.service';

// The folder tree, fetched once and shared by the rail, the Files home and
// the folder pages, the same way the rail caches its categories. `reload`
// after a file is added, so the counts move.

let cached: FileFolder[] | null = null;
const listeners = new Set<(folders: FileFolder[]) => void>();

async function fetchFolders(): Promise<FileFolder[]> {
  const folders = (await DocumentService.getFolders()) || [];
  cached = folders;
  listeners.forEach((listener) => listener(folders));
  return folders;
}

export function useFileFolders(): { folders: FileFolder[]; loaded: boolean; reload: () => Promise<void> } {
  const [folders, setFolders] = useState<FileFolder[]>(cached ?? []);
  const [loaded, setLoaded] = useState(cached !== null);

  useEffect(() => {
    const listener = (next: FileFolder[]) => { setFolders(next); setLoaded(true); };
    listeners.add(listener);
    if (cached === null) fetchFolders().catch(() => setLoaded(true));
    return () => { listeners.delete(listener); };
  }, []);

  const reload = useCallback(async () => { await fetchFolders().catch(() => undefined); }, []);
  return { folders, loaded, reload };
}

/** Children of a folder (or the top level, for null), in server order. */
export function childrenOf(folders: FileFolder[], parentKey: string | null): FileFolder[] {
  return folders.filter((f) => (f.parentKey ?? null) === parentKey);
}

/** The folder and everything above it, top first, for the breadcrumb. */
export function pathTo(folders: FileFolder[], key: string): FileFolder[] {
  const byKey = new Map(folders.map((f) => [f.key, f]));
  const trail: FileFolder[] = [];
  for (let at = byKey.get(key); at; at = at.parentKey ? byKey.get(at.parentKey) : undefined) trail.unshift(at);
  return trail;
}

/** For tests: forget the cache between renders. */
export function resetFolderCache(): void { cached = null; }
